import React, { useCallback, useEffect, useState } from "react";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

/**
 * @param {object} props
 * @param {string} [props.id]
 * @param {string} [props.initialValue]
 * @param {(text: (string)) => void} props.onChange
 * @param {(text: (string)) => void} [props.onEnter]
 * @param {boolean} [props.onEnterDisabled]
 * @param {boolean} [props.multiline]
 * @param {boolean} [props.autoFocus]
 * @param {number} [props.minRows]
 * @param {boolean} [props.required]
 * @param {string} [props.label]
 * @param {string} [props.helperText]
 * @param {string|React.ReactNode} [props.helperText2]
 * @param {("text"|"number")} [props.type]
 * @param {("left"|"right")} [props.textAlign]
 * @param {("small"|"medium")} [props.size]
 * @param {boolean} [props.borderless]
 * @param {boolean} [props.disabled]
 * @param {(text: string) => string|undefined} [props.sanitize] - Function to sanitize input. If returns undefined, input is invalid.
 * @param {number} [props.maxLength] - Maximum length of input. Shows character count when provided.
 * @param {string|undefined} [props.autoComplete] - Auto-complete attribute.
 * @param {boolean} [props.isValid] - Whether the input is valid.
 */
export const SmartTextField = ({
  id,
  initialValue = "",
  onChange,
  onEnterDisabled = true,
  onEnter,
  multiline = false,
  autoFocus = false,
  required = false,
  helperText = "",
  helperText2 = "",
  minRows = 1,
  label,
  type = "text",
  textAlign = "left",
  size = "medium",
  borderless = false,
  disabled = false,
  sanitize,
  maxLength,
  autoComplete,
  ...props
}) => {
  const [name, setName] = useState(initialValue);
  const [changed, setHasChanged] = useState(false);
  const [isValid, setValidStatus] = useState(true);

  const onChangeHandler = (ev) => {
    setHasChanged(true);
    setName(ev.target.value);
  };

  const onKeyDownHandler = (ev) => {
    if (onEnterDisabled) {
      return;
    }
    if (ev.key !== "Enter") {
      return;
    }

    if (onEnter) {
      onEnter(name);
    }
  };

  const validate = useCallback(() => {
    if (`${name}`.length === 0) {
      onChange("");
      setValidStatus(false);
      return false;
    }

    // If sanitize function is provided, use it to validate the input
    if (sanitize) {
      const sanitizedValue = sanitize(name);
      if (sanitizedValue === undefined) {
        setValidStatus(false);
        return false;
      }
      setValidStatus(true);
      onChange(sanitizedValue);
      return true;
    }

    setValidStatus(true);
    onChange(name);
    return true;
  }, [setValidStatus, name, onChange, sanitize]);

  useEffect(() => {
    if (!changed) {
      return;
    }

    validate();
    setHasChanged(false);
  }, [validate, changed]);

  return (
    <Box>
      <TextField
        id={id}
        defaultValue={initialValue}
        fullWidth
        variant={borderless ? "standard" : "outlined"}
        type={type}
        margin="dense"
        multiline={multiline}
        minRows={minRows}
        autoFocus={autoFocus}
        label={borderless ? helperText : label}
        size={size}
        required={required}
        helperText={helperText2}
        disabled={disabled}
        inputProps={maxLength ? { maxLength } : undefined}
        {...props}
        error={!isValid}
        autoComplete={autoComplete}
        onChange={onChangeHandler}
        onKeyDown={onKeyDownHandler}
        sx={{
          "& input": { textAlign },
          "& textarea": {
            minHeight: "100px",
          },
        }}
      />
      {maxLength && (
        <Box display="flex" justifyContent="space-between" mt={0.5}>
          <Box>
            <Typography variant="caption" color={isValid ? "text.secondary" : "error"}>
              {!isValid && name.length > 0 ? "Unsupported characters found." : ``}
            </Typography>
          </Box>
          <Typography variant="caption" color={name.length > maxLength ? "error" : "text.secondary"}>
            {name.length}/{maxLength}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
